import { IAttrValue, IRowData } from "@multiweb/types/contracts";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { AttrValueReader } from "OW.Remoting/Remoting/AttrValueReader";
import { IDataSourceExtended } from "../../../../Global/Base/DataSources/Interfaces";
import { registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { CalendarGrid } from "../../CalendarGrid";
import { AggregatedDataSource } from "../../DataSource/AggregatedDataSource";
import { ICalendarItemData } from "../../ICalendarTooltip";
import { CalendarItem } from "../Columns/CalendarItem";
import { DataRow } from "./DataRow";
import { TableCell } from "./TableCell";

export class CalendarDataRow extends DataRow implements ICalendarItemData {
    private _uniqeId: string;

    protected override _dataGrid: CalendarGrid;

    public get uniqueId(): string {
        if (this.checkAndLogDisposedUsage("get uniqueId")) return this._uniqeId;

        return this._uniqeId;
    }

    public set uniqueId(v: string) {
        if (this.checkAndLogDisposedUsage("set uniqueId")) return;

        this._uniqeId = v;
    }

    /*Interface ICalendarItemData*/
    protected _caption: string;
    protected _condition: string;
    protected _includeTime: boolean;
    protected _showFormID: string;
    protected _objectName: string;
    protected _color: string;
    protected _dateFrom: Date;
    protected _dateTo: Date;
    protected _textFields: string[] = [];
    protected _textFieldsValues: string[] = [];

    public get caption(): string {
        if (this.checkAndLogDisposedUsage("get caption")) return this._caption;

        return this._caption;
    }

    public get condition(): string {
        if (this.checkAndLogDisposedUsage("get condition")) return this._condition;

        return this._condition;
    }

    public get includeTime(): boolean {
        if (this.checkAndLogDisposedUsage("get includeTime")) return this._includeTime;

        return this._includeTime;
    }

    public get showFormID(): string {
        if (this.checkAndLogDisposedUsage("get showFormID")) return this._showFormID;

        return this._showFormID;
    }

    public override get dataKey(): string {
        if (this.checkAndLogDisposedUsage("get dataKey")) return this._dataKey;

        return this._dataKey;
    }

    public get objectName(): string {
        if (this.checkAndLogDisposedUsage("get objectName")) return this._objectName;

        return this._objectName;
    }

    public get color(): string {
        if (this.checkAndLogDisposedUsage("get uniqueId")) return this._uniqeId;

        return this._color;
    }

    public get dateFrom(): Date {
        if (this.checkAndLogDisposedUsage("get dateFrom")) return this._dateFrom;

        return this._dateFrom;
    }

    public get dateTo(): Date {
        if (this.checkAndLogDisposedUsage("get dateTo")) return this._dateTo;

        return this._dateTo;
    }

    public get textFields(): string[] {
        if (this.checkAndLogDisposedUsage("get textFields")) return this._textFields;

        return this._textFields;
    }

    public get textFieldsValues(): string[] {
        if (this.checkAndLogDisposedUsage("get textFieldsValues")) return this._textFieldsValues;

        return this._textFieldsValues;
    }

    public static readonly dataKeySplitterChar = "|"; //znak, podle kterého se určí která část dataKey je uniqueID datového zdroje a která původní dataKey.

    /**
     * Nastaví komplexní datový klíč, který se skládá z datového zdroje a záznamu.
     * @param uniqueId Unikátní ID datového zdroje.
     * @param originalDataKey Klíč záznamu.
     */
    protected _setDataKey(uniqueId: string, originalDataKey: string): void {
        if (this.checkAndLogDisposedUsage("_setDataKey")) return;

        this._dataKey = CalendarDataRow.getDataKey(uniqueId, originalDataKey); //vytvoří string uniqueID|dataKey
    }

    /**
     * Vrátí složený klíč, který se skládá z datového zdroje a záznamu.
     * @param dataSourceUID Unikátní ID datového zdroje.
     * @param itemDataKey Klíč záznamu.
     */
    public static getDataKey(dataSourceUID: string, itemDataKey: string): string {
        return `${dataSourceUID}${CalendarDataRow.dataKeySplitterChar}${itemDataKey}`;
    }

    /**
     * Vrací uniqueId a původní dataKey záznamu.
     */
    public static getDataKeyComponents(complexDataKey: string): [string, string] {
        if (complexDataKey.indexOf(CalendarDataRow.dataKeySplitterChar) !== -1) {
            const [uid, key] = complexDataKey.split(CalendarDataRow.dataKeySplitterChar);
            return [uid, key];
        } else
            return [undefined, undefined];
    }

    private get _column(): CalendarItem {
        if (this.checkAndLogDisposedUsage("get _column")) return undefined;

        return this._dataGrid.getCalendarItemById(this._uniqeId);
    }

    /**
     * Ziniciuje nový řádek
     * @param dataRow Data k zobrazení v řádku
     * @param ownerGrid DataGrid, ke kterému řádek patří
     * @param controlElement Volitelný element s ovládacími prvky, který se vloží do nulté buňky řádku
     */
    public override init(dataRow: IRowData, ownerGrid: CalendarGrid, controlElement: HTMLElement): void {
        if (this.checkAndLogDisposedUsage("init")) return;

        this._init(dataRow, ownerGrid, controlElement);
        this._initCells(dataRow);
    }

    /**
     * Základní inicializace vlastností řádku
     * @param dataRow řádek
     * @param ownerGrid rodičovský DataGrid
     * @param controlElement element s ovládacími prvky, který se vloží do nulté buňky řádku
     */
    protected override _init(dataRow: IRowData, ownerGrid: CalendarGrid, controlElement: HTMLElement): void {
        if (this.checkAndLogDisposedUsage("_init")) return;

        this._dataGrid = ownerGrid;
        this._dataCells = new Array<TableCell>();
        this._dataKey = dataRow[(this._dataGrid.dataSource as AggregatedDataSource<IDataSourceExtended>).getDataKey(this._uniqeId)].valueOf();
        if (this.dataKey == undefined)
            throw Error("DataRow has invalid dataKey");

        if (controlElement) {
            this._controlElement = controlElement;
            this._controlElement.addEventListener("widthChanged", () => {
                this.dispatchEvent(new CustomEvent("controlElementWidthChanged", {}));
            });
        }
    }

    /**
     * Inicializace datových buněk řádku
     * @param dataRow řádek
     */
    protected override _initCells(dataRow: IRowData): void {
        if (this.checkAndLogDisposedUsage("_initCells")) return;

        if (this._column == null)
            return;

        const attrNames: Array<string> = [
            this._column.dateFromFld,
            this._column.dateToFld,
            this._column.captionFld,
        ];

        let i = 0;
        const dataRowCaseInsensitive = ObjectUtils.getPropertiesCaseInsensitive<IAttrValue>(dataRow ?? {});

        this._dateFrom = AttrValueReader.read(dataRowCaseInsensitive.get(this._column.dateFromFld));
        this._dateTo = AttrValueReader.read(dataRowCaseInsensitive.get(this._column.dateToFld));
        this._caption = AttrValueReader.read(dataRowCaseInsensitive.get(this._column.captionFld));

        for (const attrName of attrNames) {
            const cell = new TableCell(this._dataGrid, this, i++, this._column, [dataRowCaseInsensitive.get(attrName)]);
            cell.validityChanged.add((sender, validity) => {
                this._onCellValidityChanged();
                this.cellValidityChanged.trigger(sender, validity);
            });
            this._dataCells.push(cell);
        }
    }

    protected override _createNoEditableCells(): void {
        if (this.checkAndLogDisposedUsage("_createNoEditableCells")) return;

        this.appendChild(this._createFirstCell());

        if (!this.dataCells)
            return;

        for (const cell of this.dataCells) {
            if (cell.column.visible) {
                cell.toEdit(false);
                const newCell = cell.render();
                if (newCell) //sloupec mohl změnit viditelnost a render vrátil null.
                    this.appendChild(newCell);
            }
        }
    }

    protected override _dispatchSelectionChangeEvent(rowID: string, data: any, uniqueId: string = this.objectName, complexDataKey: string = rowID): void {
        if (this.checkAndLogDisposedUsage("_dispatchSelectionChangeEvent")) return;

        const e = new CustomEvent("SelectionChanged", { detail: { dataGrid: this._dataGrid, rowID: rowID, data: data, uniqueId: uniqueId, complexDataKey: complexDataKey }, bubbles: true, cancelable: true });
        this._dataGrid.dispatchEvent(e);
    }

    public override async selectWithCheckChanges(): Promise<void> {
        if (this.checkAndLogDisposedUsage("selectWithCheckChanges")) return;

        if (!(await this._checkIfCanSelectionChange()))
            return;

        if (!this.isSelected || (this.isSelected && (this._dataGrid.selectedRows.length > 1))) {
            this._dataGrid.focus();
            this._dataGrid.isSelectedAll = false;
            this.isSelected = true;
            this._dataGrid.lastClickedRow = this;
            const [uniqueId, originalDataKey] = CalendarDataRow.getDataKeyComponents(this.dataKey);
            this._dispatchSelectionChangeEvent(originalDataKey, { doubleClick: false, clickedRow: this }, uniqueId, this.dataKey);
        }
    }
}

registerCustomElement("tsc-CalendarDataRow", CalendarDataRow);
